import React, { useEffect, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { Row, Col, Button } from "reactstrap";
import FormSelect from "../../form-components/SelectComponent";
import FormInput from "../../form-components/InputComponent";
import Swal from "sweetalert2";

interface SelectOptions {
  label: string;
  value: string;
}

interface BOQMaterialDrawerProps {
  materialCategoryOptions: any[];
  categoryOptions: SelectOptions[];
  materialOptions: SelectOptions[];
  blockArea: number;
  currentMaterial: any;
  onSave: (data: any) => void;
  onCancel: () => void;
  setValue: any;
  watch: any;
  selectedBOQLineIndex: number | null;
  boqLines: any[];
  getValues: any;
}

const BOQMaterialDrawer: React.FC<BOQMaterialDrawerProps> = ({
  materialCategoryOptions,
  categoryOptions,
  materialOptions,
  blockArea,
  currentMaterial,
  onSave,
  onCancel,
  setValue,
  watch,
  selectedBOQLineIndex,
  boqLines,
  getValues,
}) => {
  const methods = useForm({
    defaultValues: currentMaterial || {
      categoryId: "",
      materialId: "",
      quantitySet: "",
      uomId: "",
      standardPrice: "",
    },
    mode: "onChange",
  });

  const {
    handleSubmit,
    watch: watchLocal,
    setValue: setValueLocal,
    reset,
    formState: { errors },
  } = methods;

  const [uomOptions, setUomOptions] = useState<SelectOptions[]>([]);
  const selectedCategoryId = watchLocal("categoryId");
  const selectedMaterialId = watchLocal("materialId");
  const quantity = watchLocal("quantitySet");

  useEffect(() => {
    if (currentMaterial) {
      reset(currentMaterial);
      if (currentMaterial.materialId) {
        fetchUom(currentMaterial.materialId, currentMaterial.uomId);
      }
    } else {
      reset({
        categoryId: "",
        materialId: "",
        quantitySet: "",
        uomId: "",
        standardPrice: "",
      });
      setUomOptions([]);
    }
  }, [currentMaterial, reset]);

  useEffect(() => {
    if (selectedMaterialId) {
      fetchUom(selectedMaterialId, null);
    }
  }, [selectedMaterialId]);

  const fetchUom = async (materialId: any, existingUomId: any = null) => {
    try {
      const materialUom = materialCategoryOptions?.find(
        (item: any) => item.materialID == materialId
      );
      if (!materialUom) return;

      const _uomOptions = [
        {
          value: materialUom?.unitOfMeasure,
          label: materialUom?.unitOfMeasureName,
        },
      ];

      if (
        materialUom?.secondaryUnitOfMeasure &&
        materialUom?.unitOfMeasure != materialUom?.secondaryUnitOfMeasure
      ) {
        _uomOptions.push({
          value: materialUom?.secondaryUnitOfMeasure,
          label: materialUom?.secondaryUnitOfMeasureName,
        });
      }

      setUomOptions(_uomOptions);

      if (!existingUomId) {
        setValueLocal("uomId", materialUom?.unitOfMeasure);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const onSubmit = (data: any) => {
    // Check for duplicate material if not editing
    if (
      selectedBOQLineIndex !== null &&
      currentMaterial?.materialIndex === undefined
    ) {
      const boqLine = getValues(`boqLines.${selectedBOQLineIndex}`);
      const existingMaterials = boqLine?.materials || [];
      const isDuplicate = existingMaterials.some(
        (m: any) => m.materialId === data.materialId
      );
      if (isDuplicate) {
        Swal.fire({
          icon: "error",
          title: "Error!",
          text: "This material already exists in this BOQ Line. Please select a different material.",
        });
        return;
      }
    }

    const materialName =
      materialOptions.find((item: any) => item.value == data.materialId)
        ?.label || "";
    const categoryName =
      categoryOptions.find((item: any) => item.value === data.categoryId)
        ?.label || "";
    const uomName =
      uomOptions.find((item: any) => item.value === data.uomId)?.label || "";

    onSave({
      ...data,
      materialName,
      categoryName,
      uomName,
    });
  };

  // Filter materials by selected category
  const availableMaterials = selectedCategoryId
    ? materialCategoryOptions
        .filter((item: any) => item.category == selectedCategoryId)
        .map((item: any) => ({
          label: item.productName,
          value: item.materialID.toString(),
        }))
    : [];

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          methods.handleSubmit(onSubmit)(e);
        }}
      >
        <Row className="mb-3">
          <Col lg="6">
            <FormSelect
              name="categoryId"
              label="Category"
              required={true}
              options={categoryOptions}
              placeholder="Select Category"
            />
          </Col>
          <Col lg="6">
            <FormSelect
              name="materialId"
              label="Material"
              required={true}
              options={availableMaterials}
              placeholder="Select Material"
              disabled={!selectedCategoryId}
            />
          </Col>
        </Row>

        <Row className="mb-3">
          <Col lg="6">
            <FormInput
              name="quantitySet"
              label="Standard Qty"
              type="number"
              required={true}
              placeholder="Enter Standard Qty"
            />
          </Col>
          <Col lg="6">
            <FormSelect
              name="uomId"
              label="UOM"
              required={true}
              options={uomOptions}
              placeholder="Select UOM"
              disabled={!selectedMaterialId}
            />
          </Col>
        </Row>

        <Row className="mb-3">
          <Col lg="6">
            <FormInput
              name="standardPrice"
              label="Standard Price"
              type="number"
              placeholder="Enter Standard Price"
            />
          </Col>
        </Row>

        <div style={{ marginTop: 20, display: "flex", gap: 10 }}>
          <Button
            color="primary"
            type="submit"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            Save
          </Button>
          <Button
            color="secondary"
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onCancel();
            }}
          >
            Cancel
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};

export default BOQMaterialDrawer;
